import React from 'react';

/**
 * Drop-in replacement for `@loadable/component`'s default export, built on
 * `React.lazy` + `React.Suspense`.
 *
 * Why a shim instead of using `React.lazy` directly at every call site:
 * - `React.lazy` *requires* a `<Suspense>` ancestor or it throws; `loadable`
 *   renders an inline fallback (default `null`) and never forces the parent to
 *   suspend. This shim wraps each lazy component in its own `<Suspense>` so the
 *   ~60 JSX-parser bindings in `ModuleJsxContent.tsx` keep behaving exactly as
 *   they did under loadable (no global boundary, no parent suspension).
 * - `React.lazy` only accepts a module with a `default` export; `loadable`
 *   supports a `resolveComponent` option to pick a named export. This shim
 *   normalizes both shapes.
 *
 * Supported subset of the loadable API actually used in this codebase:
 *   loadable(() => import('x'))
 *   loadable(() => import('x'), { fallback: <Spinner /> })
 *   loadable(() => import('x'), { resolveComponent: (m) => m.Named })
 */
interface LoadableOptions<P> {
  fallback?: React.ReactNode;
  resolveComponent?: (module: any, props: P) => React.ComponentType<P>;
}

export default function loadable<P = any>(
  loader: () => Promise<any>,
  options: LoadableOptions<P> = {},
): React.FC<P> {
  const { fallback = null, resolveComponent } = options;

  const Lazy = React.lazy(async () => {
    const mod = await loader();
    const Component: React.ComponentType<P> = resolveComponent
      ? resolveComponent(mod, {} as P)
      : mod && mod.default !== undefined
      ? mod.default
      : mod;
    return { default: Component };
  });

  const Loadable: React.FC<P> = (props) => (
    <React.Suspense fallback={fallback}>
      <Lazy {...(props as any)} />
    </React.Suspense>
  );

  // Preserve a no-op `preload` so any `.preload()` call (none active today, but
  // referenced in commented code) does not crash if re-enabled.
  (Loadable as any).preload = () => loader();

  return Loadable;
}
